import { useEffect, useRef, useState } from 'react'
import './rewards.styles.scss'

import { useDispatch, useSelector } from 'react-redux';
import ViewRewardsModal from './view-rewards-modal.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import RewardItemUnclicked from './reward-item-unclicked.component';
import RewardItemClicked from './reward-item-clicked.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAward, faBoxOpen, faGem, faGift, faShoppingCart, faUpDown } from '@fortawesome/free-solid-svg-icons';
import { UseCloudGameTransactions } from '@/utils/firebase.utils';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import { getActivityRecordsQuery } from '@/utils/getDataFromDb';
import { setNewStuffCount } from '@/state/slices/contentSlice';
import useAnalytics from '@/utils/useAnalytics';
import { useLocation } from 'react-router-dom';
import { gameActivityComplete } from '@/components/PREVIEW/previewGameActivity';
import { changeDocumentTitle } from '@/utils/useDocumentTitle';
import { useGameTerms } from '@/utils/gameTerms';

const PlayerRewards = ({
  gameData,
  playerData,
  updatePlayerData,
  gameElements,
  onMomentumTriggered,
}) => {

  const screenSize = useSelector(state => state.contentData.screenSize);
  const terms = useGameTerms();
  const [rewardModalIsOpen, setRewardModalIsOpen] = useState(false);
  const [modalActivity, setModalActivity] = useState(null);
  const [activityToDisplay, setActivityToDisplay] = useState(null);
  const [modalHeadText, setModalHeadText] = useState(null);
  const [notificationCounts, setNotificationCounts] = useState(null);
  const [rewardsToOpen, setRewardsToOpen] = useState(null);
  const [viewCategory, setViewCategory] = useState('reward');
  const [moreLimit] = useState(12);
  const [noMore, setNoMore] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [alertMessage, setAlertMessage] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);
  const { trackEvent, trackException } = useAnalytics();
  const activityRef = useRef();
  const rewardRef = useRef();
  const approveRef = useRef();
  const viewRef = useRef();
  const pendingMomentumRef = useRef([]);
  const momentumTimerRef = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    if (!gameData?.gameName) return;
    changeDocumentTitle(`Player Rewards | ${gameData?.gameName}`);
  }, [gameData?.gameName]); 

  useEffect(() => {
    if (!gameData || !playerData || !viewCategory) { return; }
    // let newActivityObj = {};
    // const content = Object.values(playerActivity)
    // .filter(a => a.display === viewCategory);
    // for (let a of content) {
    //     newActivityObj[a.id] = a;
    // }
    // setActivityToDisplay(newActivityObj)
    if (viewRef.current !== viewCategory) {
      activityRef.current = {};
      viewRef.current = viewCategory;
    }
    getRewardsToDisplay();
  }, [gameData, playerData, viewCategory])

  useEffect(() => {
    if (!playerData || !playerData.newStuff) return;
    rewardRef.current = [];
    let countObj = {
      'reward': 0,
      'achievement': 0,
      'prizepack': 0,
      'purchase': 0
    }
    const newStuffIds = new Set(playerData.newStuff.map(n => n.id));
    for (let a of playerData.newStuff) {
      // Don't count entries that are still hidden behind an unopened parent
      // reward/prizepack — they get counted once the parent is opened and
      // this effect re-runs. This mirrors the display filter in
      // filterOutUnviewedRewards so the badge matches what's on the page.
      const isHidden =
        Array.isArray(a.hideUntilOpened) &&
        a.hideUntilOpened.some(parentId => newStuffIds.has(parentId));
      if (!isHidden && countObj[a.display] !== undefined) {
        countObj[a.display]++;
      }
      // rewardsToOpen still tracks every unopened entry so "open all" clears
      // the full chain in one action
      rewardRef.current.push(a.id);
    }
    setRewardsToOpen([...rewardRef.current]);
    // console.log(countObj);
    setNotificationCounts(countObj);
  }, [playerData])

  useEffect(() => {
    const pageTitle = document.getElementById('page-title');
    if (pageTitle) {
      pageTitle.innerText = 'Player Rewards'
    }
  }, []);

  function getRewardsToDisplay(more) {
    let filter = [{
      'key': 'display',
      'value': viewCategory
    },
    {
      'key': 'playerId',
      'value': playerData.playerId
    }]
    const queryObj = {
      'filter': filter,
      'path': 'activityRecords',
      'gameId': gameData.gameId,
      'limit': moreLimit,
      'once': true,
      'callback': callback
    }
    if (more) {
      // get last entry and add it to queryObj
      queryObj.lastEntry = Object.values(activityRef.current)
        .sort((a, b) => a.ts < b.ts ? 1 : -1)
      [Object.values(activityRef.current).length - 1]
    }
    if (location.pathname.includes('/preview/player/')) {
      const prevActivity = Object.values(gameActivityComplete);
      // sort through filter for correct category and playerId
      const filteredActivity = prevActivity.filter(a =>
        a.display === viewCategory &&
        a.playerId === playerData.playerId
      )
        .sort((a, b) => a.ts < b.ts ? 1 : -1)
        .slice(0, moreLimit);
      let newActivityObj = {};
      for (let a of filteredActivity) {
        newActivityObj[a.id] = a;
      }
      activityRef.current = {
        ...activityRef.current,
        ...newActivityObj
      }
      setActivityToDisplay(filterHiddenRewards(activityRef.current));
      setNoMore(true);
      return;
    }
    getActivityRecordsQuery(queryObj)
    function callback(data) {
      activityRef.current = {
        ...activityRef.current,
        ...data
      };
      filterOutUnviewedRewards();
    }
  }

  // Hide any reward whose parent reward/prizepack is still unopened.
  // The backend stamps a `hideUntilOpened` array (parent ids) onto each
  // newStuff entry; while a parent id is still present in newStuff (i.e.
  // not yet opened), its children stay hidden. Opening a reward removes it
  // from newStuff, which reveals the next layer down the chain. Shared by the
  // live query path and the preview path so they can't drift apart.
  function filterHiddenRewards(activityObj) {
    const newStuff = (playerData && playerData.newStuff) || [];
    const newStuffIds = new Set(newStuff.map(n => n.id));
    const displayObj = {};
    for (let d of Object.values(activityObj)) {
      const entry = newStuff.find(n => n.id === d.id);
      if (
        entry &&
        Array.isArray(entry.hideUntilOpened) &&
        entry.hideUntilOpened.some(parentId => newStuffIds.has(parentId))
      ) {
        continue;
      }
      displayObj[d.id] = d;
    }
    return displayObj;
  }

  function filterOutUnviewedRewards() {
    // keep activityRef.current as the full fetched set (pagination + refetch
    // rely on it); only the displayed state is filtered
    setActivityToDisplay(filterHiddenRewards(activityRef.current));
    if (Object.keys(activityRef.current).length < moreLimit) {
      setNoMore(true);
    } else {
      setNoMore(false);
    }
  }

  async function handleRewardModalOpen(activityId) {
    const act = activityRef.current[activityId];
    let tempPlayerData = { ...playerData };

    // launch reward modal
    setModalActivity(act)
    setModalHeadText(
      act.display === 'prizepack'
        ? 'New ' + act.title + '!'
        : 'Your New Rewards!'
    )
    setRewardModalIsOpen(true);

    // if activity is not in newStuff, return (already viewed)
    if (!tempPlayerData.newStuff.find(n => n.id === activityId)) {
      console.log('reward already viewed: ', activityId);
      return;
    }

    // Momentum items always appear as clicked; MomentumRevealModal owns their markRewardAsViewed.
    if (act.display === 'momentum') return;

    // save new count of unviewed rewards
    let savedNewStuffCount = Number(window.localStorage.getItem(`${playerData.playerId}_newRewardCount`));
    savedNewStuffCount--;
    window.localStorage.setItem(`${playerData.playerId}_newRewardCount`, savedNewStuffCount);
    dispatch(setNewStuffCount(savedNewStuffCount));

    // Mark the reward as viewed on the server

    const timeout = setTimeout(async () => {
      const data = {
        'gameId': gameData.gameId,
        'playerId': playerData.playerId,
        'rewardIdArr': [activityId]
      }
      // console.log(data);

      // Mark the reward as viewed on the client
      tempPlayerData.newStuff = tempPlayerData.newStuff.filter(n => n.id !== activityId);
      

      if (!location.pathname.includes('/preview/player/')) {
        updatePlayerData(tempPlayerData);
        const res = await UseCloudGameTransactions('markRewardAsViewed', data)
        // console.log(res);
        if (res.error) {
          setAlertMessage(`Something went wrong. Try again later. (Error: ${res.error})`);
          trackException(`Error marking reward ${activityId} as viewed: ${res.error}`);
          return;
        }
      } else {
        // In preview mode, we won't update the server, but we still want to 
        // update the local state to reflect that the reward has been viewed
        updatePlayerData(tempPlayerData);
      }

      // track event in analytics
      trackEvent(
        'reward_viewed',
        'Player',
        `Player viewed reward ${activityId} in game ${gameData.gameId}`,
        1,
        null
      )

      // checkForEarnedLevels(gameElements, tempPlayerData, gameData);

      if (act.data && Object.values(act.data).filter(d => d.id === 'xp').length > 0) {
        const els = document.querySelectorAll('.points-div');
        els.forEach(e => {
          e.classList.add('flash');
        });
      }
      if (act.data && Object.values(act.data).filter(d => d.id === 'currency').length > 0) {
        const els = document.querySelectorAll('.currency-div');
        els.forEach(e => {
          e.classList.add('flash');
        });
      }
      clearTimeout(timeout);
    }, 2000);

    const ptEls = document.querySelectorAll('.points-div');
    ptEls.forEach(e => {
      e.classList.remove('flash');
    });
    const currencyEls = document.querySelectorAll('.currency-div');
    currencyEls.forEach(e => {
      e.classList.remove('flash');
    });

    // For rewards and achievements, reveal any pending momentum cards at the 3s mark
    // (when the GIF ends) before the reward data is shown.
    if (act.display === 'reward' || act.display === 'achievement') {
      pendingMomentumRef.current = (playerData.newStuff ?? [])
        .filter(n => n.type === 'momentum')
        .map(n => n.id);
      if (pendingMomentumRef.current.length > 0) {
        momentumTimerRef.current = setTimeout(() => {
          momentumTimerRef.current = null;
          // setRewardModalIsOpen(false);
          const ids = [...pendingMomentumRef.current];
          pendingMomentumRef.current = [];
          onMomentumTriggered?.(ids);
        }, 2000);
      }
    }
    filterOutUnviewedRewards();
  }

  function handleRewardModalClose() {
    setRewardModalIsOpen(false);
    // If the player closes early, cancel the scheduled trigger and fire immediately.
    if (momentumTimerRef.current) {
      clearTimeout(momentumTimerRef.current);
      momentumTimerRef.current = null;
    }
    const ids = [...pendingMomentumRef.current];
    pendingMomentumRef.current = [];
    if (ids.length > 0) onMomentumTriggered?.(ids);
  }

  async function clearNewRewards() {
    if (location.pathname.includes('/preview/player/')) {
      setAlertMessage('This is a preview of how the "Open All Rewards" process ' +
        'will look for players. In the live game, once you click "Open All Rewards", ' +
        'all of your unviewed rewards will be marked as viewed and you will receive ' +
        `any points, ${terms.currency.pluralLower}, or items in those rewards. You will also see a ` +
        'notification for each reward on your home page, and you can click into each ' +
        'one to see more details.');
      return;
    }
    setConfirmMessage('Are you sure you want to open all of the unopened rewards?');
    approveRef.current = () => {
      continueClear();
    }

    async function continueClear() {
      setClearLoading(true);
      const rewardsToClear = [...rewardsToOpen];
      // console.log(rewardsToClear);
      const res = await UseCloudGameTransactions(
        'markRewardAsViewed',
        {
          'gameId': gameData.gameId,
          'playerId': playerData.playerId,
          'rewardIdArr': rewardsToClear,
        }
      )
      // console.log(res);
      setClearLoading(false);
      if (res.error) {
        setAlertMessage(`Something went wrong. Try again later. (Error: ${res.error})`);
        trackException(`Error marking all rewards as viewed: ${res.error}`);
        return;
      }
      rewardRef.current = [];
      setRewardsToOpen(rewardRef.current);
      trackEvent(
        'reward_viewed_bulk',
        'Player',
        `Player viewed all rewards in game ${gameData.gameId}`,
        1,
        null
      )
    }
  }


  return (
    <div className='player-rewards g-card'>
      <div className='player-title'>
        <ImageContainer
          src='/images/icons/treasure.png'
          alt='rewards icon'
          className='player-title-icon' />
        Your Rewards
      </div>
      <hr />
      <div className={`reward-grid ${screenSize}`}>
        <div className='left-menu'>
          <button
            className={`g-button med-btn reward-button ${viewCategory === 'reward' ? 'primary' : ''}`}
            onClick={() => setViewCategory('reward')}>
            <div className={`notification-with-badge ${(!notificationCounts || !notificationCounts.reward || notificationCounts.reward === 0) ? 'no-badge' : ''}`}>
              {
                (
                  screenSize !== 'mobile'
                ) &&
                'Rewards'
              }
              <FontAwesomeIcon icon={faGem} size="lg" />
              {
                (notificationCounts && notificationCounts.reward > 0) &&
                <span className="badge">{notificationCounts.reward}</span>
              }

            </div>
          </button>
          <button
            className={`g-button med-btn reward-button ${viewCategory === 'achievement' ? 'primary' : ''}`}
            onClick={() => setViewCategory('achievement')} >
            <div className={`notification-with-badge ${(!notificationCounts || !notificationCounts.achievement || notificationCounts.achievement === 0) ? 'no-badge' : ''}`}>
              
              {
                (
                  screenSize !== 'mobile'
                ) &&
                'Achievements'
              }
              <FontAwesomeIcon icon={faAward} size="lg" />
              {
                (notificationCounts && notificationCounts.achievement > 0) &&
                <span className="badge">{notificationCounts.achievement}</span>
              }
            </div>
          </button>
          {
            (
              gameData &&
              gameData.access.includes('prizepacks') &&
              gameElements &&
              Object.values(gameElements).filter(e =>
                e.status === 'active' &&
                e.opt_itemType === 'prizepack'
              ).length > 0
            ) &&
            <button
              className={`g-button med-btn reward-button ${viewCategory === 'prizepack' ? 'primary' : ''}`}
              onClick={() => setViewCategory('prizepack')} >
              <div className={`notification-with-badge ${(!notificationCounts || !notificationCounts.prizepack || notificationCounts.prizepack === 0) ? 'no-badge' : ''}`}>
                
                {
                  (
                    screenSize !== 'mobile'
                  ) &&
                  'Prize Packs'
                }
                <FontAwesomeIcon icon={faGift} size="lg" />
                {
                  (notificationCounts && notificationCounts.prizepack > 0) &&
                  <span className="badge">{notificationCounts.prizepack}</span>
                }
              </div>
            </button>
          }
          {
            (gameData.access.includes('store')) &&
            <button
              className={`g-button med-btn reward-button ${viewCategory === 'purchase' ? 'primary' : ''}`}
              onClick={() => setViewCategory('purchase')} >
              <div className={`notification-with-badge ${(!notificationCounts || !notificationCounts.purchase || notificationCounts.purchase === 0) ? 'no-badge' : ''}`}>
                
                {
                  (
                    screenSize !== 'mobile'
                  ) &&
                  'Your Purchases'
                }
                <FontAwesomeIcon icon={faShoppingCart} size="lg" />
              </div>
            </button>
          }
          {
            (gameData.access.includes('momentum')) &&
            <button
              className={`g-button med-btn reward-button ${viewCategory === 'momentum' ? 'primary' : ''}`}
              onClick={() => setViewCategory('momentum')} >
              <div className={`notification-with-badge ${(!notificationCounts || !notificationCounts.momentum || notificationCounts.momentum === 0) ? 'no-badge' : ''}`}>
                
                {
                  (
                    screenSize !== 'mobile'
                  ) &&
                  'Momentum Cards'
                }
                <FontAwesomeIcon icon={faUpDown} />
              </div>
            </button>
          }
          {
            (rewardsToOpen && rewardsToOpen.length > 0) &&
            (
              (clearLoading)
                ?
                <button className='g-button small-btn'>
                  <ThreeDotsLoader />
                </button>
                :
                <button
                  className='g-button small-btn open-all-button'
                  onClick={() => clearNewRewards()} >
                  <FontAwesomeIcon icon={faBoxOpen} />
                  {
                    (
                      screenSize !== 'mobile'
                    ) &&
                    <div className='meta '>Open All Rewards</div>
                  }
                </button>
            )
          }
        </div>

        <div className='right-col'>
          {
            (
              screenSize === 'mobile' ||
              screenSize === 'tablet'
            ) &&
            <div className='rewards-mobile-head'>
              <h2>
                {
                  viewCategory === 'reward' &&
                  'Rewards'
                }
                {
                  viewCategory === 'achievement' &&
                  'Achievements'
                }
                {
                  viewCategory === 'prizepack' &&
                  'Prize Packs'
                }
                {
                  viewCategory === 'purchase' &&
                  'Purchases'
                }
              </h2>
              <hr />
            </div>
          }
          <div className={`reward-list-grid ${screenSize}`}>
            {
              (activityToDisplay && playerData) &&
              Object.values(activityToDisplay)
                .sort((a, b) => a.ts < b.ts ? 1 : -1)
                .map(act => (
                  (
                    playerData.newStuff &&
                    playerData.newStuff.find(a => a.id === act.id) &&
                    act.display !== 'purchase' &&
                    act.display !== 'momentum'
                  )
                    ?
                    <div key={act.ts} >
                      <RewardItemUnclicked
                        reward={act}
                        gameData={gameData}
                        playerData={playerData}
                        handleRewardModalOpen={handleRewardModalOpen} />
                    </div>
                    :
                    <div key={act.ts}>
                      <RewardItemClicked
                        reward={act}
                        handleRewardModalOpen={handleRewardModalOpen} />
                    </div>
                ))
            }
          </div>
          <div className="g-space-1"></div>
          {
            (activityToDisplay) &&
            <div className='center-col'>
              <button
                className={`g-button med-btn ${noMore ? 'disabled' : ''}`}
                onClick={() => getRewardsToDisplay(true)}>
                Load More
              </button>
            </div>
          }
        </div>
      </div>
      <ViewRewardsModal
        playerData={playerData}
        isOpen={rewardModalIsOpen}
        onClose={() => handleRewardModalClose()}
        activity={modalActivity}
        width={'normal'}
        closeButton={true}
        modalHeadText={modalHeadText}
        gameElements={gameElements} />
      <ModalConfirm
        show={confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
      <ModalAlert
        show={alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage} />
    </div>
  )
}

export default PlayerRewards